import React, { useRef, useState } from "react";
import { HttpStatusCode } from "axios";

import { checkNicknameDuplicate } from "../../services/authServices";

import CommonButton from "../../components/Button";
import CommonInput from "../../components/Input/CommonInput";

type Props = { placeholder?: string; subLabel?: string };

export type NickNameVerificationData = {
  nickname: string;
};

export default function useNickName({
  placeholder = "변경할 별명을 입력해주세요",
  subLabel = "",
}: Props): [
  {
    isComplete: boolean;
    data: NickNameVerificationData;
    reset: () => void;
    validate: () => boolean;
  },
  JSX.Element
] {
  const [data, setData] = useState<NickNameVerificationData>({ nickname: "" });
  const [isComplete, setIsComplete] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const inputRef = useRef<HTMLInputElement>(null);

  const reset = () => {
    setData((prev) => ({ ...prev, nickname: "" }));
    setIsComplete(false);
    setError("");
  };

  const nicknameValidation = (dryrun: boolean = false) => {
    if (data.nickname === "") {
      if (dryrun === false) {
        setError("별명을 입력해주세요.");
        inputRef.current?.focus();
      }
      return false;
    }
    if (data.nickname.length > 10) {
      if (dryrun === false) {
        setError("별명은 10자 이하이어야 합니다.");
        inputRef.current?.focus();
      }
      return false;
    }
    if (/^[a-zA-Z0-9ㄱ-ㅎㅏ-ㅣ가-힣]*$/.test(data.nickname) === false) {
      if (dryrun === false) {
        setError("별명은 한글, 영문, 숫자만 사용 가능합니다.");
        inputRef.current?.focus();
      }
      return false;
    }

    return true;
  };

  const validate = () => {
    if (data.nickname === "") {
      setError("별명을 입력해주세요.");
      inputRef.current?.focus();
      return false;
    }

    if (isComplete === false) {
      setError("별명 중복 확인을 해주세요.");
      inputRef.current?.focus();
      return false;
    }

    if (!nicknameValidation()) return false;

    return true;
  };

  return [
    { isComplete, data, reset, validate },
    <>
      <div className="flex flex-col gap-2">
        <div className=" text-sm text-brand-neutral-60 flex items-center gap-1.5 leading-[18px]">
          별명
          {subLabel && <span className="text-xs leading-4">{subLabel}</span>}
        </div>
        <CommonInput
          placeholder={placeholder}
          className="rounded-[3px]"
          postfix={{
            content: (
              <CommonButton
                variant={
                  nicknameValidation(true) && !isComplete
                    ? "primary"
                    : "tertiary-s"
                }
                onClick={async () => {
                  if (!nicknameValidation()) return;

                  try {
                    const res = await checkNicknameDuplicate(data.nickname);
                    if (
                      res.status === HttpStatusCode.Ok &&
                      res.data.isDuplicated === false
                    ) {
                      setIsComplete(true);
                      setError("사용 가능한 별명입니다.");
                    } else {
                      setError(
                        "이미 사용 중인 별명입니다. 다른 별명을 입력해주세요."
                      );
                    }
                  } catch (e: any) {
                    setError(
                      "이미 사용 중인 별명입니다. 다른 별명을 입력해주세요."
                    );
                  }
                }}
                className="whitespace-nowrap text-[13px] leading-[17px] rounded-[3px] h-[32px] px-2.5 py-[11px]"
              >
                중복 확인
              </CommonButton>
            ),
            className: "pr-1",
          }}
          value={data.nickname}
          onChange={(text: string) => {
            setData({ ...data, nickname: text });
            setIsComplete(false);
            setError("");
          }}
          ref={inputRef}
          error={error}
        />
      </div>
    </>,
  ];
}
